import { useState } from "react";
import { Image } from "expo-image";
import { useRouter } from "expo-router";
import { Platform, StyleSheet, View } from "react-native";

import { UpgradePrompt } from "~/components/composed/UpgradePrompt";
import {
  AppText,
  Button,
  ButtonText,
  EmptyState,
  Stack,
} from "~/components/ui";
import { ATTACHMENT_KIND_LABELS } from "~/features/items/labels";
import {
  captureImage,
  pickImage,
  useSignedUrl,
  uploadImage,
} from "~/features/media/storage";
import { useAuth } from "~/hooks/useAuth";
import { usePlan } from "~/hooks/usePlan";
import { useItemActions, useItemAttachments } from "~/state/items";
import {
  fonts,
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import type {
  Item,
  ItemAttachment,
  ItemReminder,
} from "~/api/generated/types.gen";

type AttachmentKind = ItemAttachment["kind"];

function AttachmentTile({
  path,
  kind,
  filename,
  onRemove,
}: {
  path: string;
  kind: AttachmentKind;
  filename: string | null;
  onRemove: () => void;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const signed = useSignedUrl("item-attachments", path);

  return (
    <View style={stylesheet.tile}>
      {kind === "photo" && signed.data ? (
        <Image
          source={{ uri: signed.data }}
          style={stylesheet.image}
          contentFit="cover"
          accessibilityLabel="Item photo"
        />
      ) : (
        <View style={[stylesheet.image, stylesheet.imagePlaceholder]}>
          <AppText size="xs" weight="semibold">
            {ATTACHMENT_KIND_LABELS[kind]}
          </AppText>
          <AppText size="xs">
            {filename ?? (signed.isLoading ? "Loading" : "File")}
          </AppText>
        </View>
      )}
      <View style={stylesheet.tileActions}>
        <AppText size="xs">{ATTACHMENT_KIND_LABELS[kind]}</AppText>
        <Button variant="text" compact onPress={onRemove}>
          <ButtonText>Remove</ButtonText>
        </Button>
      </View>
    </View>
  );
}

export function ItemAttachments({ itemId }: { itemId: string }) {
  const stylesheet = useThemedStyles(makeStyles);
  const { user } = useAuth();
  const router = useRouter();
  const plan = usePlan();
  const attachments = useItemAttachments(itemId);
  const photos = attachments.filter(
    (attachment) => attachment.kind === "photo",
  );
  const actions = useItemActions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const photoAllowance = plan.photosFor(photos.length);

  async function add(kind: AttachmentKind, source: "library" | "camera") {
    if (!user) return;
    setError(null);
    setBusy(true);
    try {
      const image =
        source === "camera" ? await captureImage() : await pickImage();
      if (!image) return;
      const path = await uploadImage("item-attachments", user.id, image);
      actions.addAttachment(
        itemId,
        path,
        kind,
        `${kind}.${image.mimeType.split("/")[1] ?? "jpg"}`,
      );
    } catch (uploadError) {
      setError(
        uploadError instanceof Error
          ? uploadError.message
          : "Unable to add that file. Uploads need a connection.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!user) {
    return (
      <Stack gap="md">
        <EmptyState
          title="Attachments need an account"
          description="Photos, receipts and manuals are stored online. Create a free account to add them."
        />
        <View style={stylesheet.actions}>
          <Button
            variant="outline"
            iconName="user-plus"
            onPress={() => router.push("/register" as never)}
          >
            <ButtonText>Create free account</ButtonText>
          </Button>
        </View>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      {attachments.length === 0 ? (
        <EmptyState
          title="No attachments yet"
          description="Add a photo of the tool, or a receipt once you are on Pro."
        />
      ) : (
        <View style={stylesheet.grid}>
          {attachments.map((attachment) => (
            <AttachmentTile
              key={attachment.id}
              path={attachment.path}
              kind={attachment.kind}
              filename={attachment.filename}
              onRemove={() => actions.removeAttachment(attachment.id)}
            />
          ))}
        </View>
      )}

      {error ? (
        <AppText size="xs" tone="danger">
          {error}
        </AppText>
      ) : null}

      <View style={stylesheet.actions}>
        {photoAllowance.allowed ? (
          <>
            <Button
              variant="outline"
              iconName="image"
              disabled={busy}
              onPress={() => void add("photo", "library")}
            >
              <ButtonText>{busy ? "Uploading..." : "Add photo"}</ButtonText>
            </Button>
            {Platform.OS !== "web" ? (
              <Button
                variant="outline"
                iconName="camera"
                disabled={busy}
                onPress={() => void add("photo", "camera")}
              >
                <ButtonText>Take photo</ButtonText>
              </Button>
            ) : null}
          </>
        ) : (
          <AppText size="xs">{photoAllowance.message}</AppText>
        )}
      </View>

      {plan.canUseReceipts ? (
        <View style={stylesheet.actions}>
          <Button
            variant="outline"
            iconName="receipt"
            disabled={busy}
            onPress={() => void add("receipt", "library")}
          >
            <ButtonText>Add receipt</ButtonText>
          </Button>
          <Button
            variant="outline"
            iconName="file-text"
            disabled={busy}
            onPress={() => void add("manual", "library")}
          >
            <ButtonText>Add manual</ButtonText>
          </Button>
        </View>
      ) : (
        <UpgradePrompt
          title="Unlock the receipt vault"
          reason="GearSync Pro stores unlimited receipts, manuals, and photos against every item."
        />
      )}
    </Stack>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    grid: { flexDirection: "row", flexWrap: "wrap", gap: spacing.m },
    tile: { width: 160, gap: spacing.xs },
    image: {
      width: 160,
      height: 160,
      borderRadius: radii.m,
      backgroundColor: colors.surface,
    },
    imagePlaceholder: {
      alignItems: "center",
      justifyContent: "center",
      gap: spacing.xs,
      padding: spacing.s,
    },
    tileActions: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: spacing.xs,
    },
    actions: { flexDirection: "row", flexWrap: "wrap", gap: spacing.s },
    filename: { fontFamily: fonts.mono },
  });
